import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/lib/supabase";
import {
  getOptimizedImageUrl,
  ImagePresets,
} from "@/lib/utils/supabaseImageTransform";
import { useAuthStore } from "@/store";
import { useImageLibraryStore } from "@/store/imageLibraryStore";
import { useThreadStore } from "@/store/threadStore";
import { Download } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Generate short UUID-like ID from long ID (deterministic hash)
const generateShortId = (longId) => {
  if (!longId) return "";
  let hash = 0;
  for (let i = 0; i < longId.length; i++) {
    const char = longId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).padStart(8, "0").substring(0, 8);
};

export const LibraryPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [images, setImages] = useState([]);
  const { user } = useAuthStore();
  const { threads, fetchUserThreads } = useThreadStore();
  const {
    images: cachedImages,
    isCacheValid,
    setImages: setCachedImages,
  } = useImageLibraryStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem("library-scroll");
    if (savedScroll) {
      window.scrollTo(0, parseInt(savedScroll));
    }

    return () => {
      sessionStorage.removeItem("library-scroll");
    };
  }, []);

  // Load images - either from cache or via RPC
  const loadImages = useCallback(async () => {
    if (!user?.id) return;

    // Check cache first
    if (isCacheValid() && cachedImages.length > 0) {
      setImages(cachedImages);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // Use existing threads if available, otherwise fetch them
      let userThreads = threads;
      if (!userThreads || userThreads.length === 0) {
        await fetchUserThreads(user.id, {
          preventAutomaticCreation: true,
          setCurrentThread: true,
        });
        userThreads = useThreadStore.getState().threads;
      }

      const userThreadIds = userThreads.map((thread) => thread.id);

      if (userThreadIds.length === 0) {
        setImages([]);
        setCachedImages([]);
        return;
      }

      // Fetch precomputed images via RPC
      const { data, error } = await supabase.rpc("get_user_images_by_threads", {
        thread_ids: userThreadIds,
      });

      if (error) {
        console.error("Error loading images via RPC:", error);
        setImages([]);
        return;
      }

      const mappedImages =
        (data || []).map((item) => {
          const toolCallData =
            typeof item.tool_call_data === "string"
              ? (() => {
                  try {
                    return JSON.parse(item.tool_call_data);
                  } catch {
                    return null;
                  }
                })()
              : item.tool_call_data || null;

          const toolName =
            item.tool_name ||
            toolCallData?.function?.name ||
            toolCallData?.name ||
            "image_gen";
          const isEditedImage =
            toolName === "image_edit" || toolName?.includes("image_edit");

          const prompt =
            item.image_prompt ||
            item.image_title ||
            item.image_name ||
            (isEditedImage ? "Edited Image" : "Generated Image");

          const id = item.tool_call_id
            ? `${item.message_id}-${item.tool_call_id}`
            : item.message_id;

          const thumbnailUrl = getOptimizedImageUrl(
            item.image_url,
            ImagePresets.thumbnail
          );

          const threadTitle =
            userThreads.find((thread) => thread.id === item.thread_id)?.title ||
            "Untitled Chat";

          return {
            id,
            url: item.image_url,
            thumbnailUrl,
            prompt,
            toolName,
            createdAt: item.created_at || new Date().toISOString(),
            threadId: item.thread_id,
            threadTitle,
            messageId: item.message_id,
            metadata: toolCallData || {},
            imageSize: item.image_size,
            imageModel: item.image_model,
            imageProvider: item.image_provider,
            isDirectUrl: item.is_direct_url,
            storagePath: item.storage_path,
            storageBucket: item.storage_bucket,
            isEditedImage,
          };
        }) || [];

      // Deduplicate by id (in case RPC returns duplicates)
      const uniqueImages = Array.from(
        new Map(mappedImages.map((img) => [img.id, img])).values()
      );

      uniqueImages.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setImages(uniqueImages);
      // Cache the results for faster subsequent loads
      setCachedImages(uniqueImages);
    } catch (error) {
      console.error("Error loading threads and images for library:", error);
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    user?.id,
    threads,
    fetchUserThreads,
    isCacheValid,
    cachedImages,
    setCachedImages,
  ]);

  // Trigger load on mount
  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Handle download
  const handleDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "generated-image.png";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  // Handle image click to open lightbox
  const handleImageClick = (image) => {
    // Save scroll position
    sessionStorage.setItem("library-scroll", window.scrollY.toString());

    // Navigate to lightbox with short UUID and pass state for instant rendering
    const shortId = generateShortId(image.id);
    navigate(`/library/image/${shortId}`, {
      state: {
        backgroundLocation: location,
        imageData: image,
        allImages: images,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full bg-purple-500/70 animate-bounce"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-3 h-3 rounded-full bg-purple-500/70 animate-bounce"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-3 h-3 rounded-full bg-purple-500/70 animate-bounce"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
          <p className="text-sm text-foreground/70">
            Loading your image library...
          </p>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium mb-2">No images generated yet</h3>
          <p className="text-muted-foreground">
            Start generating images in your chats and they&apos;ll appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-background">
      <ScrollArea className="flex-1">
        <div className="py-4">
          {/* Image Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 px-2">
            {images.map((image) => (
              <div
                key={image.id}
                className="group relative aspect-square rounded-lg overflow-hidden transition-all duration-200 cursor-pointer"
                onClick={() => handleImageClick(image)}
              >
                {/* Image */}
                <img
                  src={image.thumbnailUrl || image.url}
                  alt={image.prompt}
                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />

                {/* Actions - download button at bottom left */}
                <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 bg-transparent hover:bg-black/20 border-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      const filename = `${image.prompt.substring(0, 30).replace(/[^a-zA-Z0-9]/g, "_")}_${image.id}.png`;
                      handleDownload(image.url, filename);
                    }}
                  >
                    <Download className="h-5 w-5 text-white" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

LibraryPage.displayName = "LibraryPage";

export default LibraryPage;
